import React, { useState, useMemo, useEffect, useRef } from 'react';
import T from '../styles/theme';
import { Badge, Btn, fmtDollar } from '../components/UI';
import Icon from '../components/Icon';
import { useStore, useFieldOptions } from '../data/store';
import { useAuth } from '../data/auth';
import { useToast } from '../components/Toast';
import FieldEditor from '../components/FieldEditor';
import { PropRow } from '../components/contacts/ContactForm';
import QuickActionPanel from '../components/QuickActionPanel';
import { EditActivityModal } from '../components/EditActivityModal';
import ChangeHistorySection from '../components/ChangeHistorySection';
import CompanyContactPicker from '../components/CompanyContactPicker';

const cfDateTime = (d) => { if (!d) return "—"; const dt = new Date(d); const m = String(dt.getMonth()+1).padStart(2,"0"); const dy = String(dt.getDate()).padStart(2,"0"); const y = String(dt.getFullYear()).slice(-2); let h = dt.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; const mn = String(dt.getMinutes()).padStart(2,"0"); return m+"/"+dy+"/"+y+" "+h+":"+mn+" "+ap; };

// industryOptions is now read inside the component via useFieldOptions
// — see industryOptions hook call below. Module-level const removed because
// hooks can only be called inside components, and the same Industry list is
// shared across Contact + Company records (override-aware via Tier B).
const SIZE_OPTIONS = ["1-10","11-50","51-200","201-500","501-1000","1000+"];

function ActIcon({ type }) {
  const map = { call: "phone", email: "mail", meeting: "users", note: "edit", lifecycle: "refresh", task: "check" };
  return (
    <div style={{ width: 30, height: 30, borderRadius: 15, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", flexShrink: 0 }}>
      <Icon name={map[type] || "dot"} size={13} color="#FAFAFA" />
    </div>
  );
}

function Section({ title, children, count, actions, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen !== false);
  return (
    <div style={{ borderBottom: "1px solid " + T.bdr }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 10, color: T.txt }}><Icon name={open ? "chevronD" : "chevronR"} size={10} color={T.tx3} /></span>
          <span style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{title}</span>
          {count !== undefined && <span style={{ fontSize: 10, color: T.txt }}>({count})</span>}
        </div>
        {actions && <div onClick={e => e.stopPropagation()}>{actions}</div>}
      </div>
      {open && <div style={{ padding: "0 14px 12px" }}>{children}</div>}
    </div>
  );
}

export default function CompanyRecord({ companyId, onBack, onSelectContact, onSelectDeal, isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const toast = useToast();
  // Tier B options — Industry list is shared with contacts (override-aware).
  const industryOptions = useFieldOptions("contacts", "industry");

  const company = useMemo(
    () => (store.companies || []).find(c => c.id === companyId),
    [store.companies, companyId]
  );

  const companyContacts = useMemo(
    () => (store.contacts || []).filter(c => c.company_id === companyId),
    [store.contacts, companyId]
  );
  const companyContactIds = useMemo(
    () => companyContacts.map(c => c.id),
    [companyContacts]
  );
  // Include deals linked directly via company_id OR indirectly via a contact
  // who belongs to this company. Some deals created before commit 1's
  // backfill may only have contact_id populated, not company_id.
  const companyDeals = useMemo(
    () => (store.deals || []).filter(d =>
      d.company_id === companyId ||
      (d.ct && companyContactIds.includes(d.ct))
    ),
    [store.deals, companyId, companyContactIds]
  );
  // Tasks are scoped to contacts (task.ct = contact_id); aggregate across
  // this company's contacts.
  const companyTasks = useMemo(
    () => (store.tasks || []).filter(t => t.ct && companyContactIds.includes(t.ct)),
    [store.tasks, companyContactIds]
  );
  // Attachments live in contact.properties.attachments. Collect from every
  // contact of the company, tagging each with its source contact id/name for
  // context (shown as subtext under the file name).
  const companyAttachments = useMemo(
    () => companyContacts.flatMap(c =>
      (c.properties?.attachments || []).map(a => ({
        ...a,
        _contactId: c.id,
        _contactName: [(c.fn || ""), (c.ln || "")].join(" ").trim(),
      }))
    ),
    [companyContacts]
  );
  // Last 5 notes across the company's contacts. Uses store.activities with
  // type='note' and the UI `date` field produced by dbActivityToLocal.
  const companyNotes = useMemo(
    () => (store.activities || [])
      .filter(a => a.type === "note" && a.contactId && companyContactIds.includes(a.contactId))
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5),
    [store.activities, companyContactIds]
  );

  const openDeals = companyDeals.filter(d => d.stage !== "Closed Won" && d.stage !== "Closed Lost");
  const wonDeals = companyDeals.filter(d => d.stage === "Closed Won");
  const openDealVal = openDeals.reduce((a, d) => a + (d.val || 0), 0);
  const wonVal = wonDeals.reduce((a, d) => a + (d.val || 0), 0);

  // Permission gates
  const canEdit = auth.hasPermission?.("companies_edit") !== false;
  const canDelete = auth.hasPermission?.("companies_delete") === true;

  // Actions dropdown
  // Quick-action state: { type, contactId?, companyId?, contactPhone?, contactName? }
  const [showQuickAction, setShowQuickAction] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  // Call picker — asks the user which contact to log the call against
  const [showCallPicker, setShowCallPicker] = useState(false);

  const openQuickForCompany = (type) => setShowQuickAction({ type, companyId });
  const openCallPicker = () => setShowCallPicker(true);
  const onPickContactForCall = (ct) => {
    setShowCallPicker(false);
    setShowQuickAction({
      type: "call",
      contactId: ct.id,
      contactPhone: ct.ph || "",
      contactName: [(ct.fn || ""), (ct.ln || "")].join(" ").trim(),
    });
  };

  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = useRef(null);
  useEffect(() => {
    if (!showActionsMenu) return;
    const handler = (e) => { if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) setShowActionsMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showActionsMenu]);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const confirmDelete = async () => {
    if (!company?.id) return;
    setDeleteBusy(true);
    try {
      await store.deleteCompany(company.id);
      setShowDeleteModal(false);
      if (onBack) onBack();
    } catch (err) {
      toast?.error?.("Failed to delete company: " + (err.message || "unknown error"));
    } finally {
      setDeleteBusy(false);
    }
  };

  // Tags add
  const [addingTag, setAddingTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState("");
  const [tagSaving, setTagSaving] = useState(false);
  const saveNewTag = async () => {
    const t = (newTagValue || "").trim();
    if (!t) { setAddingTag(false); return; }
    if ((company?.tags || []).includes(t)) { toast?.info?.("Tag already exists"); setNewTagValue(""); return; }
    setTagSaving(true);
    try {
      const nextTags = [...(company.tags || []), t];
      await store.updateCompany(company.id, { tags: nextTags });
      setNewTagValue("");
      setAddingTag(false);
      toast?.success?.("Tag added");
    } catch (err) {
      toast?.error?.("Failed to add tag: " + (err.message || "unknown error"));
    } finally {
      setTagSaving(false);
    }
  };
  const removeTag = async (tag) => {
    if (!company?.id) return;
    const nextTags = (company.tags || []).filter(t => t !== tag);
    try { await store.updateCompany(company.id, { tags: nextTags }); }
    catch (err) { toast?.error?.("Failed to remove tag: " + (err.message || "unknown error")); }
  };

  // Activity feed tabs (derived from contacts' activities)
  const [actTab, setActTab] = useState("Activity");
  const [actSearch, setActSearch] = useState("");
  const typeMap = { Notes: "note", Emails: "email", Calls: "call", Tasks: "task", Meetings: "meeting" };
  const actTabs = ["Activity", "Notes", "Emails", "Calls", "Tasks", "Meetings"];

  // Union three sources: activities directly linked via companyId (new in
  // commit 3), activities linked to one of this company's contacts (commit 2
  // derivation), and seed activities stashed on each contact. Dedupe by id
  // since overlap is now possible (company-direct writes also land in the
  // global store.activities list).
  const filteredActs = useMemo(() => {
    const ids = new Set(companyContacts.map(c => c.id));
    const seed = companyContacts.flatMap(c => (c.activities || []).map(a => ({ ...a, _contactId: c.id })));
    const global = (store.activities || []).filter(
      a => a.companyId === companyId || (a.contactId && ids.has(a.contactId))
    ).map(a => ({ ...a, source: "global" }));
    const byId = new Map();
    // Seed first so global entries can overwrite them (global has canonical id).
    for (const a of seed) if (a.id != null) byId.set(a.id, a);
    for (const a of global) if (a.id != null) byId.set(a.id, a);
    // Activities without ids (legacy seed entries) fall through as-is.
    const seedNoId = seed.filter(a => a.id == null);
    let acts = [...byId.values(), ...seedNoId].sort((a, b) => new Date(b.date) - new Date(a.date));
    if (actTab !== "Activity") acts = acts.filter(a => a.type === typeMap[actTab]);
    if (actSearch) acts = acts.filter(a => (a.title + " " + (a.notes || "")).toLowerCase().includes(actSearch.toLowerCase()));
    return acts;
  }, [companyContacts, store.activities, actTab, actSearch, companyId]);

  // Loading / not-found states
  if (!store.companies) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.tx3, fontSize: 12 }}>
        Loading...
      </div>
    );
  }
  if (!company) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 14, color: T.txt, marginBottom: 12 }}>Company not found</div>
        <button onClick={onBack} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Back to Companies</button>
      </div>
    );
  }

  const ownerOptions = (store.teamMembers || []).map(m => ({ value: m._dbId, label: m.name }));
  const ownerName = store.getOwnerName?.(company.owner || company.ownerId) || "—";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "transparent" }}>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", flex: 1, overflow: isMobile ? "auto" : "hidden" }}>

        {/* LEFT SIDEBAR */}
        <div style={{ width: isMobile ? "100%" : 300, flexShrink: 0, borderRight: isMobile ? "none" : "1px solid " + T.bdr, borderBottom: isMobile ? "1px solid " + T.bdr : "none", overflowY: isMobile ? "visible" : "auto", background: "#2C2E33" }}>
          <div style={{ padding: isMobile ? 12 : 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <button onClick={onBack} style={{ background: "none", border: "none", color: "#FAFAFA", fontSize: 11, fontWeight: 400, display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontFamily: "inherit" }}>
                <Icon name="chevronL" size={14} color="#FAFAFA" /> Companies
              </button>
              <div ref={actionsMenuRef} style={{ position: "relative" }}>
                <button onClick={e => { e.stopPropagation(); setShowActionsMenu(v => !v); }} style={{ padding: "2px 6px", borderRadius: 3, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>Actions ▾</button>
                {showActionsMenu && (
                  <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, minWidth: 160, background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.4)", zIndex: 20, padding: 4 }}>
                    {canDelete ? (
                      <button onClick={() => { setShowActionsMenu(false); setShowDeleteModal(true); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", fontSize: 11, background: "transparent", border: "none", color: "#E53935", cursor: "pointer", borderRadius: 4, fontFamily: "inherit" }}>Delete company</button>
                    ) : (
                      <div style={{ padding: "6px 10px", fontSize: 10, color: T.tx3 }}>No actions available</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 8, background: "rgba(255,255,255,0.06)", border: "1px solid " + T.bdr, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 400, color: "#FAFAFA", flexShrink: 0 }}>{(company.name || "?").charAt(0).toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FieldEditor
                  label="Company name"
                  value={company.name || ""}
                  type="text"
                  readOnly={!canEdit}
                  onSave={v => store.updateCompany(company.id, { name: v })}
                  renderDisplay={v => <div style={{ fontSize: 18, fontWeight: 400, color: T.txt, cursor: canEdit ? "pointer" : "default", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v || "Untitled company"}</div>}
                />
                <FieldEditor
                  label="Industry"
                  value={company.industry || ""}
                  type="select"
                  options={industryOptions}
                  readOnly={!canEdit}
                  onSave={v => store.updateCompany(company.id, { industry: v })}
                  renderDisplay={v => <div style={{ fontSize: 11, color: T.tx3, cursor: canEdit ? "pointer" : "default" }}>{v || "—"}</div>}
                />
              </div>
            </div>

            {/* Quick actions — Note/Email/Call/Meeting. No Task: tasks.company_id
                doesn't exist (left for a future migration). Call routes through
                CompanyContactPicker because a call belongs to a person. */}
            <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
              {[
                { l: "Note", ic: "edit", action: () => openQuickForCompany("note") },
                { l: "Email", ic: "mail", action: () => openQuickForCompany("email") },
                { l: "Call", ic: "phone", action: openCallPicker },
                { l: "Meeting", ic: "users", action: () => openQuickForCompany("meeting") },
              ].map(a => (
                <div key={a.l} onClick={a.action} style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "7px 2px", borderRadius: 4,
                  border: "1px solid " + T.bdr, background: T.card, cursor: "pointer", transition: "all 0.15s",
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = T.blue + "50"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}>
                  <Icon name={a.ic} size={15} color={T.tx2} />
                  <span style={{ fontSize: 8, color: T.txt }}>{a.l}</span>
                </div>
              ))}
            </div>

            {/* Inline quick-action panel (reused from ContactRecord) */}
            {showQuickAction && (
              <div style={{ marginBottom: 12 }}>
                <QuickActionPanel
                  type={showQuickAction.type}
                  contactId={showQuickAction.contactId}
                  companyId={showQuickAction.companyId}
                  contactPhone={showQuickAction.contactPhone}
                  contactName={showQuickAction.contactName}
                  onClose={() => setShowQuickAction(null)}
                  onCreated={() => setShowQuickAction(null)}
                />
              </div>
            )}

            {/* Stats cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, marginBottom: 14 }}>
              {[
                { l: "Contacts", v: companyContacts.length },
                { l: "Open Deals", v: openDeals.length },
                { l: "Pipeline", v: fmtDollar(openDealVal) },
                { l: "Won Revenue", v: fmtDollar(wonVal) },
              ].map(s => (
                <div key={s.l} style={{ padding: "8px 10px", borderRadius: 6, background: T.sf, border: "1px solid " + T.bdr }}>
                  <div style={{ fontSize: 9, color: T.tx3, fontWeight: 400, marginBottom: 3 }}>{s.l}</div>
                  <div style={{ fontSize: 14, fontWeight: 400, color: "#FAFAFA" }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* About this company */}
          <Section title="About this company" defaultOpen={true}>
            <PropRow label="Domain" value={company.domain} editable={canEdit} onChange={v => store.updateCompany(company.id, { domain: v })} />
            <PropRow label="Industry" value={company.industry} editable={canEdit} onChange={v => store.updateCompany(company.id, { industry: v })} type="select" options={industryOptions} />
            <PropRow label="Size" value={company.size} editable={canEdit} onChange={v => store.updateCompany(company.id, { size: v })} type="select" options={SIZE_OPTIONS} />
            <PropRow label="Annual revenue" value={company.annualRevenue} editable={canEdit} onChange={v => store.updateCompany(company.id, { annual_revenue: Number(v) || 0 })} type="currency" />
            <PropRow label="City" value={company.city} editable={canEdit} onChange={v => store.updateCompany(company.id, { city: v })} />
            <PropRow label="State/Region" value={company.state} editable={canEdit} onChange={v => store.updateCompany(company.id, { state: v })} />
            <PropRow label="Country" value={company.country} editable={canEdit} onChange={v => store.updateCompany(company.id, { country: v })} />
            <PropRow label="Phone" value={company.phone} editable={canEdit} onChange={v => store.updateCompany(company.id, { phone: v })} type="phone" />
            <PropRow label="Description" value={company.description} editable={canEdit} onChange={v => store.updateCompany(company.id, { description: v })} />
            <PropRow label="Owner" value={ownerName} editable={canEdit} onChange={v => {
              const member = (store.teamMembers || []).find(m => m.name === v);
              store.updateCompany(company.id, { owner_id: member?._dbId || null });
            }} type="select" options={(store.teamMembers || []).map(m => m.name).filter(Boolean)} />
          </Section>

          {/* Tags */}
          <Section title="Tags" count={(company.tags || []).length}>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
              {(company.tags || []).map(tag => (
                <span key={tag} onClick={() => canEdit && removeTag(tag)} style={{ cursor: canEdit ? "pointer" : "default" }} title={canEdit ? "Click to remove" : ""}>
                  <Badge color={"#3D8B8B"}>{tag}</Badge>
                </span>
              ))}
              {addingTag ? (
                <input
                  autoFocus
                  value={newTagValue}
                  onChange={e => setNewTagValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") { e.preventDefault(); saveNewTag(); }
                    else if (e.key === "Escape") { setAddingTag(false); setNewTagValue(""); }
                  }}
                  onBlur={() => { if (!tagSaving && !newTagValue.trim()) setAddingTag(false); }}
                  disabled={tagSaving}
                  placeholder="tag name"
                  style={{ padding: "2px 6px", borderRadius: 3, border: "1px solid " + T.bdr, background: "rgba(255,255,255,0.04)", color: T.txt, fontSize: 10, outline: "none", fontFamily: "inherit", width: 90 }}
                />
              ) : canEdit ? (
                <button onClick={() => setAddingTag(true)} style={{ padding: "2px 8px", borderRadius: 3, border: "1px dashed " + T.bdr, background: "transparent", color: T.txt, fontSize: 10, cursor: "pointer" }}>+ Add</button>
              ) : null}
            </div>
          </Section>

          {/* Custom fields */}
          {(store.customFields?.companies || []).length > 0 && (
            <Section title="Custom Fields" count={(store.customFields?.companies || []).length} defaultOpen={false}>
              {(store.customFields?.companies || []).map(f => {
                if (f.type === "calculation") {
                  let val = 0;
                  try {
                    let formula = f.formula || "";
                    Object.keys(company).forEach(k => { if (typeof company[k] === "number") formula = formula.replace(new RegExp("\\b" + k + "\\b", "g"), company[k]); });
                    val = Function('"use strict"; return (' + formula.replace(/[^0-9+\-*/().,%\s]/g, "") + ')')();
                    if (isNaN(val)) val = 0;
                  } catch { val = 0; }
                  const display = f.calcFormat === "currency" ? "$" + Number(val).toLocaleString() : f.calcFormat === "percent" ? Number(val).toFixed(1) + "%" : Number(val).toLocaleString();
                  return <PropRow key={f.id} label={f.label} value={display} />;
                }
                return (
                  <FieldEditor
                    key={f.id}
                    label={f.label}
                    value={company[f.id] || ""}
                    type={f.type || "text"}
                    options={f.options || []}
                    readOnly={!canEdit}
                    onSave={v => store.updateCompany(company.id, { [f.id]: f.type === "number" || f.type === "currency" ? Number(v) : v })}
                  />
                );
              })}
            </Section>
          )}
        </div>

        {/* CENTER: Activity Timeline */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
            <div style={{ display: "flex", padding: "0 16px" }}>
              {actTabs.map(t => (
                <button key={t} onClick={() => setActTab(t)} style={{
                  padding: "10px 14px", border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit",
                  color: T.txt, opacity: actTab === t ? 1 : 0.4, fontSize: 11, fontWeight: 400,
                  borderBottom: actTab === t ? "2px solid " + T.blue : "2px solid transparent",
                }}>{t}</button>
              ))}
            </div>
          </div>

          <div style={{ padding: "10px 16px", borderBottom: "1px solid rgba(217,219,221,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: "6px 12px", flex: 1 }}>
              <Icon name="search" size={13} color={T.txt} />
              <input value={actSearch} onChange={e => setActSearch(e.target.value)} placeholder="Search activities..."
                style={{ border: "none", outline: "none", background: "transparent", color: T.txt, fontSize: 12, width: "100%", fontFamily: "inherit" }} />
              <span style={{ fontSize: 10, color: T.txt, opacity: 0.4, flexShrink: 0 }}>({filteredActs.length})</span>
            </div>
          </div>

          <div style={{ flex: 1, overflow: "auto", padding: "16px 16px" }}>
            {filteredActs.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: T.txt }}>
                <div style={{ fontSize: 13 }}>No activity yet</div>
                <div style={{ fontSize: 11, color: T.tx3, marginTop: 6 }}>Activities on contacts linked to this company will appear here.</div>
              </div>
            ) : (
              filteredActs.map((act, i) => {
                // Same edit/delete affordance rules as ContactRecord/DealRecord.
                const editableTypes = ["note", "meeting", "call", "email", "lifecycle"];
                const isAdmin = auth.currentUser?.role === "admin";
                const isEditable = editableTypes.includes(act.type) && (
                  act.type === "lifecycle"
                    ? true
                    : (act.by && act.by !== "System" && (act.by === store.currentUserId || isAdmin))
                );
                return (
                  <div key={i} style={{ display: "flex", gap: 12, marginBottom: 0 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 30 }}>
                      <ActIcon type={act.type} />
                      {i < filteredActs.length - 1 && <div style={{ width: 1, flex: 1, background: T.bdr, minHeight: 16 }} />}
                    </div>
                    <div style={{ flex: 1, paddingBottom: 20 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 3 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 400, color: "#FAFAFA", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(217,219,221,0.12)", padding: "2px 8px", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>{act.type}</span>
                          <span style={{ fontSize: 12, fontWeight: 400, color: T.txt }}>{act.title}</span>
                          {act.duration && <span style={{ fontSize: 10, color: T.txt }}>{act.duration}</span>}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: 8 }}>
                          <span style={{ fontSize: 10, color: T.txt }}>{cfDateTime(act.date)}</span>
                          {isEditable && (
                            <button onClick={() => setEditingActivity(act)} title="Edit"
                              style={{ background: "transparent", border: "none", padding: 4, cursor: "pointer", opacity: 0.5, display: "flex", alignItems: "center" }}
                              onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                              onMouseLeave={e => e.currentTarget.style.opacity = "0.5"}>
                              <Icon name="edit" size={12} color={T.txt} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: T.txt, lineHeight: 1.6, padding: "4px 0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{act.notes}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div style={{ width: isMobile ? "100%" : 300, flexShrink: 0, borderLeft: isMobile ? "none" : "1px solid " + T.bdr, borderTop: isMobile ? "1px solid " + T.bdr : "none", overflowY: isMobile ? "visible" : "auto", background: "#2C2E33" }}>

          {/* Deals */}
          <Section title="Deals" count={companyDeals.length} defaultOpen={true}>
            {companyDeals.length === 0 ? (
              <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No deals</div>
            ) : companyDeals.map(d => (
              <div key={d.id} onClick={() => onSelectDeal && onSelectDeal(d.id)} style={{ padding: "8px 10px", marginBottom: 4, borderRadius: 4, background: "#2C2E33", border: "1.5px solid rgba(217,219,221,0.12)", cursor: "pointer", transition: "border-color 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.blue + "50"}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}>
                <div style={{ fontSize: 11, fontWeight: 400, color: "#FAFAFA" }}>{d.name}</div>
                <div style={{ fontSize: 10, color: T.txt, marginTop: 2 }}>Amount: <span style={{ color: "#FAFAFA", fontWeight: 400 }}>{fmtDollar(d.val)}</span></div>
                {d.close && <div style={{ fontSize: 10, color: T.txt, marginTop: 1 }}>Close: {d.close}</div>}
                <div style={{ fontSize: 10, color: T.txt, marginTop: 1 }}>Stage: <span style={{ color: "#FAFAFA", fontWeight: 400 }}>{d.stage}</span></div>
              </div>
            ))}
          </Section>

          {/* Contacts */}
          <Section title="Contacts" count={companyContacts.length} defaultOpen={true}>
            {companyContacts.length === 0 ? (
              <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No contacts</div>
            ) : companyContacts.map(ct => (
              <div key={ct.id} onClick={() => onSelectContact && onSelectContact(ct.id)} style={{ padding: "8px 10px", marginBottom: 4, borderRadius: 4, background: "#2C2E33", border: "1.5px solid rgba(217,219,221,0.12)", cursor: "pointer", transition: "border-color 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.blue + "50"}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.bdr}>
                <div style={{ fontSize: 11, fontWeight: 400, color: "#FAFAFA" }}>{ct.fn} {ct.ln}</div>
                {ct.title && <div style={{ fontSize: 10, color: T.tx3, marginTop: 2 }}>{ct.title}</div>}
                {ct.email && <div style={{ fontSize: 10, color: T.txt, marginTop: 2 }}>{ct.email}</div>}
                {ct.ph && <div style={{ fontSize: 10, color: T.tx3, marginTop: 1 }}>{ct.ph}</div>}
              </div>
            ))}
          </Section>

          {/* Tasks — aggregated across the company's contacts */}
          <Section title="Tasks" count={companyTasks.length} defaultOpen={false}>
            {companyTasks.length === 0 ? (
              <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No tasks</div>
            ) : companyTasks.map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", borderBottom: "1px solid " + T.bdr, opacity: t.done ? 0.4 : 1 }}>
                <div onClick={() => store.toggleTask && store.toggleTask(t.id)} style={{ width: 14, height: 14, borderRadius: 3, border: "2px solid " + (t.done ? "#2E8B57" : T.bdr), background: t.done ? "#2E8B57" : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#fff", cursor: "pointer" }}>
                  {t.done ? <Icon name="check" size={8} color={"#2E8B57"} /> : ""}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: T.txt, textDecoration: t.done ? "line-through" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</div>
                  <div style={{ fontSize: 9, color: T.txt }}>Due: {t.due || "—"}</div>
                </div>
                {t.pri && <Badge color={t.pri === "high" ? "#E53935" : t.pri === "medium" ? "#D4763A" : T.tx3}>{t.pri}</Badge>}
              </div>
            ))}
          </Section>

          {/* Attachments — aggregated from contact.properties.attachments */}
          <Section title="Attachments" count={companyAttachments.length} defaultOpen={false}>
            {companyAttachments.length === 0 ? (
              <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No attachments yet</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "6px 0" }}>
                {companyAttachments.map((a, i) => (
                  <a key={i} href={a.url || "#"} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 4, background: "rgba(255,255,255,0.04)", border: "1px solid " + T.bdr, textDecoration: "none", color: T.txt, fontSize: 11 }}>
                    <Icon name="file" size={12} color={T.tx3} />
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
                      {a._contactName && <div style={{ fontSize: 9, color: T.tx3, marginTop: 1 }}>from {a._contactName}</div>}
                    </div>
                    <span style={{ fontSize: 9, color: T.tx3, flexShrink: 0 }}>{a.size ? (a.size / 1024).toFixed(0) + "KB" : ""}</span>
                  </a>
                ))}
              </div>
            )}
          </Section>

          {/* Recent Notes — last 5 note-activities across company's contacts */}
          <Section title="Recent Notes" count={companyNotes.length} defaultOpen={false}>
            {companyNotes.length === 0 ? (
              <div style={{ fontSize: 11, color: T.txt, padding: "8px 0" }}>No notes yet</div>
            ) : companyNotes.map((n, i) => (
              <div key={i} style={{ padding: "6px 0", borderBottom: "1px solid " + T.bdr }}>
                <div style={{ fontSize: 11, color: T.txt }}>{n.title}</div>
                {n.notes && <div style={{ fontSize: 10, color: T.txt, marginTop: 2 }}>{n.notes}</div>}
                <div style={{ fontSize: 9, color: T.tx3, marginTop: 2 }}>{cfDateTime(n.date)}</div>
              </div>
            ))}
          </Section>

          {/* Change History — audit log for this company row */}
          <div style={{ padding: "0 14px" }}>
            <ChangeHistorySection entityType="company" entityId={company.id} />
          </div>
        </div>
      </div>

      {/* ═══ CALL CONTACT PICKER ═══ */}
      <CompanyContactPicker
        isOpen={showCallPicker}
        onClose={() => setShowCallPicker(false)}
        contacts={companyContacts}
        onSelect={onPickContactForCall}
        title="Call which contact?"
      />

      {/* ═══ DELETE CONFIRMATION MODAL ═══ */}
      {showDeleteModal && (
        <div onClick={() => !deleteBusy && setShowDeleteModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: "92vw", background: "#2C2E33", border: "1px solid " + T.bdr, borderRadius: 10, padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(229,57,53,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: "#E53935", fontSize: 14, fontWeight: 500 }}>!</div>
              <div style={{ fontSize: 15, fontWeight: 400, color: T.txt }}>Delete {company.name || "this company"}?</div>
            </div>
            <div style={{ fontSize: 12, color: T.tx2, lineHeight: 1.6, marginBottom: 16 }}>
              Contacts and deals linked to this company will be unlinked (not deleted). This cannot be undone.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setShowDeleteModal(false)} disabled={deleteBusy} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + T.bdr, background: "transparent", color: T.txt, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={confirmDelete} disabled={deleteBusy} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #E53935", background: "#E53935", color: "#FAFAFA", fontSize: 11, cursor: deleteBusy ? "default" : "pointer", fontFamily: "inherit", opacity: deleteBusy ? 0.6 : 1 }}>{deleteBusy ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      <EditActivityModal
        activity={editingActivity}
        onSave={async (updates) => editingActivity ? await store.updateActivity(editingActivity.id, updates) : false}
        onDelete={async () => editingActivity ? await store.deleteActivity(editingActivity.id) : false}
        onClose={() => setEditingActivity(null)}
      />
    </div>
  );
}
